import { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import SignaturePadLib from 'signature_pad';
import { RotateCcw } from 'lucide-react';

// Export the signature as a compact JPEG instead of a full-resolution
// PNG. iPad-drawn PNG signatures ran 200-800KB each; a PI intake with
// 6+ signed forms became a multi-megabyte POST that stalled and failed
// on clinic wifi ("Failed to submit", Juana Perez 2026-07-17). A 600px
// JPEG on a white background is ~15-30KB and prints identically.
function compressSignature(canvas) {
  try {
    const maxW = 600;
    const scale = Math.min(1, maxW / canvas.width);
    const out = document.createElement('canvas');
    out.width = Math.round(canvas.width * scale);
    out.height = Math.round(canvas.height * scale);
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out.toDataURL('image/jpeg', 0.82);
  } catch {
    return canvas.toDataURL('image/png');
  }
}

const SignaturePad = forwardRef(function SignaturePad({ onSign, signerName, onNameChange }, ref) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  // The endStroke listener below is registered once (empty-dep effect),
  // so it must read the CURRENT onSign through a ref. The pinned
  // first-render closure was the Mary Ann Statler bug (2026-07-31):
  // the kiosk reused one pad across forms, every stroke reported to
  // the FIRST form's slot, and the other consent forms submitted
  // unsigned while looking signed on screen.
  const onSignRef = useRef(onSign);
  useEffect(() => { onSignRef.current = onSign; });

  // Expose getData() to parent via ref
  useImperativeHandle(ref, () => ({
    getData: () => {
      if (padRef.current && !padRef.current.isEmpty()) {
        return compressSignature(canvasRef.current);
      }
      return null;
    },
    isEmpty: () => padRef.current?.isEmpty() ?? true,
  }));

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    padRef.current = new SignaturePadLib(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(20, 20, 20)',
      minWidth: 1,
      maxWidth: 2.5,
    });

    padRef.current.addEventListener('endStroke', () => {
      setIsEmpty(padRef.current.isEmpty());
      if (onSignRef.current && !padRef.current.isEmpty()) {
        onSignRef.current(compressSignature(canvasRef.current));
      }
    });

    return () => { if (padRef.current) padRef.current.off(); };
  }, []);

  function handleClear() {
    padRef.current?.clear();
    setIsEmpty(true);
    onSign?.(null);
  }

  return (
    <div className="space-y-2">
      {/* Signer name */}
      <div>
        <label className="mb-1 block text-[13px] font-semibold text-gray-700">Print Name</label>
        <input
          type="text"
          value={signerName || ''}
          onChange={(e) => onNameChange?.(e.target.value)}
          placeholder="Full legal name"
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-primary-500 focus:outline-none"
        />
      </div>

      {/* Signature canvas */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[13px] font-semibold text-gray-700">Signature</label>
          <button onClick={handleClear} className="flex items-center gap-1 text-[12px] text-gray-500 hover:text-gray-700">
            <RotateCcw className="h-3 w-3" /> Clear
          </button>
        </div>
        <div className="rounded-xl border-2 border-gray-300 bg-white overflow-hidden" style={{ touchAction: 'none' }}>
          <canvas
            ref={canvasRef}
            className="w-full"
            style={{ height: '120px', cursor: 'crosshair' }}
          />
        </div>
        <p className="mt-1 text-[11px] text-gray-400 text-center">Sign above using your finger or stylus</p>
      </div>
    </div>
  );
});

export default SignaturePad;
